import {
  Alert,
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Popconfirm,
  Select,
  Spin,
  Typography,
} from "antd"
import Col from "antd/es/grid/col"
import Row from "antd/es/grid/row"
import dayjs from "dayjs"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"

import { classAtoms, classSessionAtoms, curriculumAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import CheckboxContentBlock from "#blocks/Form/CheckboxContentBlock"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import { NULL_ID } from "#classes/Firebase"
import FeeSelector from "#components/forms/AddClass/AddStudentToClass/FeeSelector"
import ClassSessionSelectionGrid from "#components/forms/AddClass/ClassSessionSelectionGrid"
import { useSessionSelectionState } from "#components/forms/AddClass/useSessionSelectionState"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { ScheduledEnrollmentEmail } from "#customTypes/composedTypes/tryoutTypes"
import { useIsFulfilled, useTimeout } from "#hooks/jotai/useCascadingData"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useOneDoc,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { getNow, isTodayOrFuture } from "#utils/dateHelpers"
import { type FeeBreakdown } from "#utils/feeCalculationHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import {
  getBlankAttendance,
  hasRealEnrollmentHistory,
  remapAbsentForFutureSessions,
} from "#utils/studentHelpers"
import { getSkillLevelForClassGroup } from "#utils/studentSkillHelpers"
import {
  buildEnrollmentClassQueryConfig,
  getEnrollmentSemester,
  getTryoutEnrollmentBlock,
  TRYOUT_ENROLLMENT_BLOCK_REASONS,
} from "#utils/tryoutHelpers"

/** Statuses that represent tryout or makeup attendance (excluded from enrollment counts & pricing) */
const TRYOUT_OR_MAKEUP_STATUSES: Set<string> = new Set([
  ATTENDANCE_STATUSES.TRYOUT_PRESENT,
  ATTENDANCE_STATUSES.TRYOUT_ABSENT,
  ATTENDANCE_STATUSES.TRYOUT_TARDY,
  ATTENDANCE_STATUSES.TRYOUT_ENROLLED,
  ATTENDANCE_STATUSES.MAKEUP_PRESENT,
  ATTENDANCE_STATUSES.MAKEUP_ABSENT,
  ATTENDANCE_STATUSES.MAKEUP_TARDY,
  ATTENDANCE_STATUSES.MAKEUP_ENROLLED,
])

/** Matches nothing — keeps the class subscription alive without a real query. */
const NULL_CLASS_QUERY_CONFIG: FirebaseQueryConfig = {
  queries: {
    id: { value: NULL_ID, comparator: COMPARATORS.EQUALS },
  },
}

const STEP_3_TEXT = {
  evalFeedback: {
    title: "Eval feedback with parent",
    description:
      "Lead teacher reviews the evaluation with the parent and discusses recommendations.",
  },
  swag: {
    title: "Swag given",
    description:
      "Give student branded swag (button, comic book, stickers, etc.).",
  },
  selectedSessions: {
    title: "Semester enrollment",
    description:
      "Select the sessions the student will be enrolled in for the semester.",
  },
}

const TryoutProgressStep3 = ({
  onCancelScheduledEmail,
}: {
  onCancelScheduledEmail?: (
    scheduled: ScheduledEnrollmentEmail
  ) => Promise<{ canceledCount: number; alreadyGoneCount: number }>
}) => {
  const form = Form.useFormInstance()
  const { message } = App.useApp()

  const studentId = Form.useWatch("studentId", form)
  const semester = Form.useWatch("semester", form)
  const sessionId = Form.useWatch("sessionId", form)
  const selectedClassId = Form.useWatch(["step3", "selectedClassId"], form)
  const didPassTryout = Form.useWatch(["step2", "didPassTryout"], form) as
    | boolean
    | undefined
  const isEnrollingNextSemester: boolean =
    Form.useWatch(["step3", "isEnrollingNextSemester"], form) ?? false
  // Semester the class picker lists. Ported from the makeup form's
  // isScheduledForNextSemester switch — families late in a semester often
  // enroll into the next one rather than the final weeks of the current.
  const enrollmentSemester = getEnrollmentSemester(
    semester,
    isEnrollingNextSemester
  )
  const sendReceipt = Form.useWatch(["step3", "sendReceipt"], form)
  const enrollmentReceiptSent = Form.useWatch(
    ["step3", "enrollmentReceiptSent"],
    form
  )
  const scheduledEnrollmentEmail = Form.useWatch(
    ["step3", "scheduledEnrollmentEmail"],
    form
  ) as ScheduledEnrollmentEmail | null | undefined

  // A scheduled email is only actionable while its sendAt is still in the
  // future. Mandrill won't honor cancel requests after that.
  // We tick a `now` state at the sendAt boundary so the cancel button auto-
  // deactivates without requiring a re-render from somewhere else. Otherwise
  // a modal idle past sendAt keeps showing a clickable Cancel button that
  // would land in the "already_gone" path on the server.
  const [now, setNow] = useState(() => dayjs())
  useEffect(() => {
    if (!scheduledEnrollmentEmail?.sendAt) return
    const msUntilBoundary = dayjs(scheduledEnrollmentEmail.sendAt).diff(dayjs())
    if (msUntilBoundary <= 0) return
    const timer = setTimeout(() => setNow(dayjs()), msUntilBoundary)
    return () => clearTimeout(timer)
  }, [scheduledEnrollmentEmail?.sendAt])

  const isScheduledPending = !!(
    scheduledEnrollmentEmail?.sendAt &&
    dayjs(scheduledEnrollmentEmail.sendAt).isAfter(now)
  )

  const [isCanceling, setIsCanceling] = useState(false)
  const handleCancelClick = useCallback(async () => {
    if (!scheduledEnrollmentEmail || !onCancelScheduledEmail) return
    setIsCanceling(true)
    try {
      const { canceledCount, alreadyGoneCount } = await onCancelScheduledEmail(
        scheduledEnrollmentEmail
      )
      const anyShipped = alreadyGoneCount > 0

      // Mirror the Firestore write into the form. FormWithValues only syncs
      // initialValues on mount, so without this Form.useWatch keeps returning
      // the stale pre-cancel values: the "Scheduled" alert lingers and a
      // subsequent save would re-trigger handleSendEnrollmentReceipt via the
      // stale step3.sendReceipt: true. Keep enrollmentReceiptSent=true when
      // any message was already delivered so the UI matches reality.
      form.setFieldValue(["step3", "scheduledEnrollmentEmail"], null)
      form.setFieldValue(["step3", "enrollmentReceiptSent"], anyShipped)
      form.setFieldValue(["step3", "sendReceipt"], false)

      if (anyShipped && canceledCount === 0) {
        message.warning(
          "The email had already been sent — parent received it. No action needed."
        )
      } else if (anyShipped) {
        message.warning(
          `Canceled ${canceledCount}, but ${alreadyGoneCount} email(s) had already been sent. Parent may have received a partial confirmation.`
        )
      } else {
        message.success("Scheduled email canceled")
      }
    } catch (err) {
      // Surface Mandrill's failure detail. Auth/network failures bubble up
      // with messages from the server (see categorize-send-errors); the
      // teacher needs to know the parent will still receive the email so
      // they can follow up directly.
      message.error(
        err instanceof Error
          ? `${err.message}. Please contact the parent directly.`
          : "Failed to cancel scheduled email. Please contact the parent directly."
      )
    } finally {
      setIsCanceling(false)
    }
  }, [scheduledEnrollmentEmail, onCancelScheduledEmail, form, message])

  const [hasInitializedSelection, setHasInitializedSelection] = useState(false)
  const [selectedSessionIndexes, setSelectedSessionIndexes] = useState<
    boolean[]
  >([])

  // Get student data with attendance
  const student = useGetOneStudent(studentId ?? undefined)

  // Whether the kid actually showed up to the tryout. Only PRESENT/TARDY count
  // — TRYOUT_ABSENT and pre-attendance statuses (TRYOUT_ENROLLED, etc.) do
  // not. Gates the followup email checkbox; the modal repeats this check at
  // submit time as a defensive guard.
  const hasAttendedTryout = useMemo(() => {
    if (!sessionId || !student?.attendance) return false
    const [tryoutClassIdForAttendance, sessionIndex] =
      getSessionInfoFromId(sessionId)
    const status =
      student.attendance[tryoutClassIdForAttendance]?.[sessionIndex]?.status
    return (
      status === ATTENDANCE_STATUSES.TRYOUT_PRESENT ||
      status === ATTENDANCE_STATUSES.TRYOUT_TARDY
    )
  }, [sessionId, student?.attendance])

  // Clear a stale sendReceipt flag when attendance changes back to
  // not-attended (e.g. teacher marks ABSENT after checking the box). Prevents
  // a request from sitting around armed and re-firing if attendance flips.
  useLayoutEffect(() => {
    if (!hasAttendedTryout && sendReceipt) {
      form.setFieldValue(["step3", "sendReceipt"], false)
    }
  }, [hasAttendedTryout, sendReceipt, form])

  // Get tryout class to find its curriculumId
  const tryoutClassId = sessionId
    ? getSessionInfoFromId(sessionId)[0]
    : undefined
  const tryoutClass = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    tryoutClassId
  )

  // Get the selected class (for enrollment)
  const selectedClass = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    selectedClassId
  )

  // Subscribe to class sessions for the selected class
  const selectedClassSessionIds = useMemo(
    () => selectedClass?.sessionIds ?? [],
    [selectedClass?.sessionIds]
  )
  const stableSessionIds = useStableIds(selectedClassSessionIds)
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )
  const isClassSessionsFulfilled = useIsFulfilled(
    stableSessionIds,
    classSessions
  )

  // Get curriculum for the selected class (for fee calculation)
  const curriculum = useOneDoc<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    selectedClass?.curriculumId
  )

  // Current semester: classes sharing the tryout class's curriculum.
  // Next semester: curricula are per-semester so curriculumId can't carry
  // over — match on the tryout class's group instead (same as the makeup
  // form, which widens to the whole next semester). The builder returns null
  // unless BOTH the semester and the scoping attribute are known, so the
  // query can never fall through to a group-only (all-semesters) listing.
  const tryoutClassGroup = tryoutClass?.group
  const tryoutClassCurriculumId = tryoutClass?.curriculumId
  const classQueryConfig: FirebaseQueryConfig = useMemo(
    () =>
      buildEnrollmentClassQueryConfig({
        enrollmentSemester,
        isEnrollingNextSemester,
        tryoutClass: {
          group: tryoutClassGroup,
          curriculumId: tryoutClassCurriculumId,
        },
      }) ?? NULL_CLASS_QUERY_CONFIG,
    [
      enrollmentSemester,
      isEnrollingNextSemester,
      tryoutClassGroup,
      tryoutClassCurriculumId,
    ]
  )
  const isClassQueryActive = classQueryConfig !== NULL_CLASS_QUERY_CONFIG

  const classes = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classQueryConfig
  )
  // Filtered queries don't expose "snapshot arrived", so follow the table
  // hooks' convention (useTryoutTableData, useMakeupTableData): resolved once
  // docs land or the timeout elapses. An empty next-semester result is a
  // legitimate finished state, not "still loading".
  const hasClassQueryResolved = useTimeout(3000, classes.length > 0)
  const isClassesLoading = isClassQueryActive && !hasClassQueryResolved

  // Set default selected class to tryout class if not already selected.
  // Skipped when enrolling next semester — the tryout class belongs to the
  // current one, so there is no sensible default.
  useLayoutEffect(() => {
    if (isEnrollingNextSemester) return
    if (tryoutClassId && !selectedClassId && tryoutClass) {
      form.setFieldsValue({
        step3: {
          ...form.getFieldValue("step3"),
          selectedClassId: tryoutClassId,
        },
      })
    }
  }, [
    tryoutClassId,
    selectedClassId,
    tryoutClass,
    form,
    isEnrollingNextSemester,
  ])

  // Toggling the semester invalidates a selection from the other semester.
  // Only clear once the selected class doc has loaded so we can compare;
  // an existing enrollment can't be toggled away (checkbox is disabled).
  useLayoutEffect(() => {
    if (!selectedClass || !enrollmentSemester) return
    if (selectedClass.semester === enrollmentSemester) return
    form.setFieldValue(["step3", "selectedClassId"], undefined)
    form.setFieldValue(["step3", "selectedSessionIds"], [])
  }, [selectedClass, enrollmentSemester, form])

  const classOptions = useMemo(() => {
    const options = (classes ?? []).map((classObj) => ({
      label: classObj.name,
      value: classObj.id,
    }))

    // Always include the tryout class as an option when enrolling into its
    // own semester (it should always be available).
    if (
      !isEnrollingNextSemester &&
      tryoutClass &&
      !options.some((opt) => opt.value === tryoutClass.id)
    ) {
      options.push({
        label: tryoutClass.name,
        value: tryoutClass.id,
      })
    }

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [classes, tryoutClass, isEnrollingNextSemester])

  // Calculate attendance for the selected class
  const attendanceForClass = useMemo(() => {
    if (!stableSessionIds.length || !isClassSessionsFulfilled) return []

    const blankAttendance = getBlankAttendance(stableSessionIds)
    const attendance = student?.attendance?.[selectedClassId ?? ""]
    const now = getNow().startOf("day")

    return blankAttendance.map((session, i) => {
      const originalStatus =
        attendance?.[i]?.status &&
        attendance[i].status !== ATTENDANCE_STATUSES.INVALID
          ? attendance?.[i]?.status
          : session.status

      // Remap ABSENT to ENROLLED for future sessions
      const sessionDate = classSessions[i]?.eventStart
        ? dayjs(classSessions[i].eventStart)
        : now
      const remappedStatus = remapAbsentForFutureSessions(
        originalStatus,
        sessionDate,
        now
      )

      return {
        ...session,
        status: remappedStatus,
      }
    })
  }, [
    student,
    selectedClassId,
    stableSessionIds,
    classSessions,
    isClassSessionsFulfilled,
  ])

  // Check if the student has actual enrollment (not just tryout/makeup attendance)
  const hasExistingEnrollment = useMemo(
    () =>
      attendanceForClass.some(
        ({ status }) => status === ATTENDANCE_STATUSES.ENROLLED
      ),
    [attendanceForClass]
  )

  // Why saving with sessions selected would be refused (failed tryout, or the
  // JARVIS-43 skill level). Same helper handleFinish throws from, so the
  // warning and the gate can't drift apart. Step 2 writes both didPassTryout
  // and the level straight to Firestore, so this clears live.
  const selectedClassAttendance = student?.attendance?.[selectedClassId ?? ""]
  const enrollmentBlock = useMemo(
    () =>
      getTryoutEnrollmentBlock({
        didPassTryout,
        student,
        enrollmentClass: selectedClass,
        hasExistingEnrollment: hasRealEnrollmentHistory(
          selectedClassAttendance
        ),
      }),
    [didPassTryout, student, selectedClass, selectedClassAttendance]
  )

  // Reset initialization flag and selection when class changes
  useLayoutEffect(() => {
    setHasInitializedSelection(false)
    setSelectedSessionIndexes([])
  }, [selectedClassId])

  // Initialize session selection from existing enrollment data
  // Wait for attendanceForClass to be populated (needs session docs) before deciding
  useLayoutEffect(() => {
    if (!attendanceForClass.length) return
    if (hasInitializedSelection) return

    if (hasExistingEnrollment) {
      const indexes = attendanceForClass.map(
        ({ status }) =>
          status === ATTENDANCE_STATUSES.ENROLLED ||
          status === ATTENDANCE_STATUSES.PRESENT ||
          status === ATTENDANCE_STATUSES.ABSENT ||
          status === ATTENDANCE_STATUSES.TARDY ||
          status === ATTENDANCE_STATUSES.PLANNED_ABSENCE
      )
      setSelectedSessionIndexes(indexes)
    }
    setHasInitializedSelection(true)
  }, [
    hasExistingEnrollment,
    attendanceForClass,
    hasInitializedSelection,
    selectedClassId,
  ])

  // Build session metadata with disabled reasons
  const sessionMetadata = useMemo(() => {
    return attendanceForClass.map((attendance) => {
      const isDisabled =
        attendance.status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
        attendance.status !== ATTENDANCE_STATUSES.ENROLLED

      return {
        disabledReason: isDisabled
          ? "Session has already been attended"
          : undefined,
      }
    })
  }, [attendanceForClass])

  const { newlySelectedSessions, droppedSessions, netSessionCount } =
    useSessionSelectionState(selectedSessionIndexes, attendanceForClass)

  // Sync selected session IDs to form state for persistence
  useLayoutEffect(() => {
    if (!classSessions.length || !selectedSessionIndexes.length) return

    const selectedIds = classSessions
      .filter((_, i) => selectedSessionIndexes[i])
      .map((session) => session.id)

    form.setFieldValue(["step3", "selectedSessionIds"], selectedIds)
  }, [selectedSessionIndexes, classSessions, form])

  // Filter out tryout/makeup sessions from selected count
  const enrolledSessionCount = useMemo(() => {
    return selectedSessionIndexes.filter((isSelected, i) => {
      if (!isSelected) return false
      return !TRYOUT_OR_MAKEUP_STATUSES.has(attendanceForClass[i]?.status)
    }).length
  }, [selectedSessionIndexes, attendanceForClass])

  // Auto-check/uncheck "Sessions selected" based on enrollment status
  useLayoutEffect(() => {
    const hasSelected = enrolledSessionCount > 0
    form.setFieldValue(["step3", "hasSelectedSessions"], hasSelected)
  }, [enrolledSessionCount, form])

  // Handle fee breakdown changes - sync to form
  const handleFeeBreakdownChange = useCallback(
    (breakdown: FeeBreakdown) => {
      form.setFieldValue(["step3", "feeBreakdown"], breakdown)
      form.setFieldValue(["step3", "paymentAmount"], `$${breakdown.grandTotal}`)
    },
    [form]
  )

  // Handle send receipt checkbox change
  const handleSendReceiptChange = useCallback(
    (e: { target: { checked: boolean } }) => {
      form.setFieldValue(["step3", "sendReceipt"], e.target.checked)
    },
    [form]
  )

  const enrollmentSemesterLabel = enrollmentSemester
    ? formatSemester(enrollmentSemester)
    : undefined

  const hasConfiguredNextSemester =
    !!semester && getEnrollmentSemester(semester, true) !== undefined
  const nextSemesterDisabledTooltip = hasExistingEnrollment
    ? "Student is enrolled in this class. You must unenroll them before switching semesters."
    : !isEnrollingNextSemester && !hasConfiguredNextSemester
      ? "No semester is configured after this one yet."
      : undefined

  // Calculate total sessions remaining in semester (future sessions)
  const totalSessionsRemaining = useMemo(() => {
    const now = getNow().startOf("day")
    return classSessions.filter((session) => {
      const sessionDate = dayjs(session.eventStart).startOf("day")
      return isTodayOrFuture(sessionDate, now)
    }).length
  }, [classSessions])

  // Get skill level for fee calculation
  const classGroup = curriculum?.group
  const skillLevelForFees =
    getSkillLevelForClassGroup(student, classGroup, enrollmentSemester) ||
    undefined

  // Number of sessions for pricing (excludes tryouts and makeups)
  const sessionCount = selectedSessionIndexes.filter((isSelected, i) => {
    if (!isSelected) return false
    return !TRYOUT_OR_MAKEUP_STATUSES.has(attendanceForClass[i]?.status)
  }).length

  return (
    <Flex col style={{ padding: "20px 30px" }} gap={30}>
      {/* Eval Feedback & Swag Row */}
      <Row gutter={48}>
        <Col span={12}>
          <CheckboxContentBlock
            fieldName={["step3", "hasEvalFeedback"]}
            title={STEP_3_TEXT.evalFeedback.title}
            description={STEP_3_TEXT.evalFeedback.description}
          />
        </Col>
        <Col span={12}>
          <CheckboxContentBlock
            fieldName={["step3", "hasGivenSwag"]}
            title={STEP_3_TEXT.swag.title}
            description={STEP_3_TEXT.swag.description}
          />
        </Col>
      </Row>

      {/* Session Selection & Receipt */}
      <Row gutter={30}>
        <Col span={24}>
          <CheckboxContentBlock
            fieldName={["step3", "hasSelectedSessions"]}
            title={STEP_3_TEXT.selectedSessions.title}
            description={STEP_3_TEXT.selectedSessions.description}
          >
            <Flex col gap={16} style={{ marginTop: 16 }}>
              {/* Class Selector */}
              <Flex col gap={8}>
                <Flex gap={30} style={{ alignItems: "flex-end" }}>
                  <DisabledFieldTooltip
                    disabled={hasExistingEnrollment}
                    tooltip="Student is enrolled in this class. You must unenroll them before changing classes."
                  >
                    <Form.Item
                      name={["step3", "selectedClassId"]}
                      label={
                        enrollmentSemesterLabel
                          ? `Select class (${enrollmentSemesterLabel})`
                          : "Select class"
                      }
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={
                          isEnrollingNextSemester
                            ? "Select a next-semester class to enroll student"
                            : "Select a class to enroll student"
                        }
                        options={classOptions}
                        showSearch
                        filterOption={filterFormOptions}
                        allowClear
                        style={{ width: 400 }}
                        disabled={hasExistingEnrollment}
                        loading={isClassesLoading}
                        notFoundContent={
                          isClassesLoading ? (
                            <Spin size="small" />
                          ) : isEnrollingNextSemester ? (
                            `No ${enrollmentSemesterLabel ?? "next semester"} classes found in this class group`
                          ) : undefined
                        }
                        data-testid="tryout-enrollment-class-select"
                      />
                    </Form.Item>
                  </DisabledFieldTooltip>
                  <CheckboxWrapper
                    name={["step3", "isEnrollingNextSemester"]}
                    label="Enroll in next semester"
                    size="small"
                    disabledTooltip={nextSemesterDisabledTooltip}
                  />
                </Flex>
                {isEnrollingNextSemester && (
                  <Alert
                    type="info"
                    showIcon
                    data-testid="tryout-next-semester-alert"
                    message={
                      enrollmentSemesterLabel
                        ? `Enrolling into ${enrollmentSemesterLabel} (next semester). Classes and sessions shown are for that semester, not the tryout's.`
                        : "No next semester is configured for this tryout's semester."
                    }
                  />
                )}
              </Flex>

              {/* Session Grid */}
              {selectedClass && (
                <Flex col gap={16}>
                  {!isClassSessionsFulfilled ? (
                    <Spin />
                  ) : student && attendanceForClass.length > 0 ? (
                    <>
                      {enrollmentBlock && (
                        <Alert
                          type="warning"
                          showIcon
                          data-testid={
                            enrollmentBlock.reason ===
                            TRYOUT_ENROLLMENT_BLOCK_REASONS.FAILED_TRYOUT
                              ? "tryout-enrollment-failed-alert"
                              : "tryout-enrollment-skill-level-alert"
                          }
                          data-reason={enrollmentBlock.reason}
                          message={enrollmentBlock.message}
                          description={`${enrollmentBlock.description} Saving with sessions selected is blocked until then.`}
                        />
                      )}
                      {isScheduledPending && scheduledEnrollmentEmail && (
                        <Alert
                          type="info"
                          showIcon
                          message={`Enrollment email scheduled to send ${dayjs(
                            scheduledEnrollmentEmail.sendAt
                          ).format("ddd MMM D, h:mm A")}`}
                          description="If you change the enrollment, cancel the email and resend so the parent sees the updated selection."
                          action={
                            <Popconfirm
                              title="Cancel the scheduled email?"
                              description="The parent will not receive the enrollment confirmation."
                              okText="Yes, cancel it"
                              cancelText="No"
                              onConfirm={handleCancelClick}
                            >
                              <Button
                                size="small"
                                loading={isCanceling}
                                danger
                                type="primary"
                              >
                                Cancel email
                              </Button>
                            </Popconfirm>
                          }
                        />
                      )}

                      <ClassSessionSelectionGrid
                        attendanceForClass={attendanceForClass}
                        classSessions={classSessions}
                        selectedSessions={selectedSessionIndexes}
                        setSelectedSessions={setSelectedSessionIndexes}
                        disabledMessage="Sessions that the student attended can not be changed."
                        sessionMetadata={sessionMetadata}
                        newlySelectedSessions={newlySelectedSessions}
                        droppedSessions={droppedSessions}
                        headerActions={
                          isScheduledPending ? undefined : enrollmentReceiptSent ? (
                            <Typography.Text type="success">
                              ✓ Followup already sent
                            </Typography.Text>
                          ) : (
                            <DisabledFieldTooltip
                              disabled={!hasAttendedTryout}
                              tooltip="Mark student as Present or Tardy on Step 2 before sending the followup email."
                            >
                              <Checkbox
                                checked={sendReceipt && hasAttendedTryout}
                                onChange={handleSendReceiptChange}
                                disabled={!hasAttendedTryout}
                                style={{ scale: 1.1 }}
                              >
                                Send tryout followup email
                              </Checkbox>
                            </DisabledFieldTooltip>
                          )
                        }
                      />

                      {/* Enrollment Summary */}
                      {enrolledSessionCount > 0 && (
                        <Typography.Text type="success">
                          ✓ Student will be enrolled in {enrolledSessionCount}{" "}
                          session
                          {enrolledSessionCount > 1 ? "s" : ""}
                          {netSessionCount !== 0 && (
                            <span>
                              {" "}
                              ({netSessionCount > 0 ? "+" : ""}
                              {netSessionCount} net change)
                            </span>
                          )}
                        </Typography.Text>
                      )}

                      {/* Receipt Section (shown when sendReceipt is checked
                          and the email hasn't already been scheduled or sent) */}
                      {sendReceipt &&
                        !isScheduledPending &&
                        !enrollmentReceiptSent &&
                        curriculum &&
                        sessionCount > 0 && (
                          <Flex
                            col
                            gap={16}
                            style={{
                              marginTop: 16,
                              padding: 20,
                              backgroundColor: "#fafafa",
                              borderRadius: 8,
                              border: "1px solid #d9d9d9",
                            }}
                          >
                            <Typography.Title level={5} style={{ margin: 0 }}>
                              Enrollment Receipt
                            </Typography.Title>

                            {/* Fee Selector */}
                            <FeeSelector
                              classGroup={classGroup ?? CLASS_GROUPS.CORE}
                              skillLevel={skillLevelForFees}
                              curriculumSkillLevels={curriculum.skillLevels}
                              sessionCount={sessionCount}
                              netSessionCount={netSessionCount}
                              totalSessionsRemaining={totalSessionsRemaining}
                              onFeeBreakdownChange={handleFeeBreakdownChange}
                            />

                            <Typography.Text type="secondary">
                              Receipt will be emailed when you save the form.
                            </Typography.Text>
                          </Flex>
                        )}
                    </>
                  ) : (
                    <Flex>
                      <Typography.Text type="secondary">
                        {!student
                          ? "No student selected"
                          : "No sessions available for this class"}
                      </Typography.Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          </CheckboxContentBlock>
        </Col>
      </Row>

      {/* Hidden fields for form state persistence */}
      <Form.Item
        name={["step3", "selectedSessionIds"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={["step3", "selectedClassId"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={["step3", "sendReceipt"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={["step3", "feeBreakdown"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={["step3", "paymentAmount"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={["step3", "siblingDiscount"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={["step3", "extraClassDiscount"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
    </Flex>
  )
}

export default TryoutProgressStep3
